import * as React from 'react';
import classnames from 'classnames';
import { createRecordUrl, formatDecimal, formatMoney, formatPercent } from 'utils/formatter';
import { useSelector } from 'react-redux';
import { artikelGruppenByNrSelector, einheitenByCodeSelector } from 'store/lookups/selector';
import { LieferungPosition, defaultLieferungPosition, moveUpLieferungPosition } from 'api/lieferungPosition';
import { EditIcon, DeleteIcon, UpIcon } from '@fluentui/react-icons';
import { Kunde } from 'api/kunde';
import { Lieferung } from 'api/lieferung';
import { formStyles } from 'styles/formStyles';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { getArtikelDetailId } from 'utils/helper';
import { LieferungPositionEdit } from './LieferungPositionEdit';
import { LieferungPositionDelete } from './LieferungPositionDelete';
import { LieferungPositionenHeader } from './LieferungPositionenHeader';

interface LieferungPositionenProps {
    kunde: Kunde;
    lieferung: Lieferung;
    lieferungPositionen: LieferungPosition[];
    viewMode: 'short' | 'long';
    withHeader?: boolean;
    allowEdit?: boolean;
    refetch?: () => void;
}

export const LieferungPositionen: React.FC<LieferungPositionenProps> = ({
    kunde,
    lieferung,
    lieferungPositionen,
    viewMode,
    withHeader = true,
    allowEdit = false,
    refetch,
}) => {
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
    const einheitenByCode = useSelector(einheitenByCodeSelector);

    const [lieferungPositionEditData, setLieferungPositionEditData] = React.useState<LieferungPosition | null>(null);
    const [lieferungPositionDeleteData, setLieferungPositionDeleteData] = React.useState<LieferungPosition | null>(
        null,
    );

    return (
        <>
            <table className={formStyles.positionenTable}>
                {withHeader && (
                    <LieferungPositionenHeader
                        allowEdit={allowEdit}
                        onAdd={() =>
                            setLieferungPositionEditData({
                                ...defaultLieferungPosition,
                                lNr: lieferung.nr,
                                pos: -1,
                            })
                        }
                    />
                )}
                <tbody>
                    {lieferungPositionen
                        .filter((lieferungPosition) => viewMode === 'long' || lieferungPosition.artikelGruppeNr !== 97)
                        .map((lieferungPosition) => (
                            <tr key={lieferungPosition.pos} className={formStyles.positionenRow}>
                                <td className={classnames(formStyles.positionenCell, formStyles.positionenNrCell)}>
                                    {lieferungPosition.pos}
                                </td>
                                <td
                                    className={classnames(
                                        formStyles.positionenCell,
                                        formStyles.positionenArtikelGruppeCell,
                                    )}
                                >
                                    {(artikelGruppenByNr[lieferungPosition.artikelGruppeNr] &&
                                        artikelGruppenByNr[lieferungPosition.artikelGruppeNr]?.text) ||
                                        lieferungPosition.artikelGruppeNr}
                                </td>
                                <td
                                    className={classnames(
                                        formStyles.positionenCell,
                                        formStyles.positionenArtikelNrCell,
                                    )}
                                >
                                    <span>
                                        {lieferungPosition.artikelGruppeNr && lieferungPosition.artikelNr ? (
                                            <OpenLink
                                                link={createRecordUrl(
                                                    'lager/artikel',
                                                    undefined,
                                                    getArtikelDetailId(
                                                        lieferungPosition.artikelGruppeNr,
                                                        lieferungPosition.artikelNr,
                                                    ),
                                                    undefined,
                                                    false,
                                                )}
                                                label={lieferungPosition.artikelNr}
                                            />
                                        ) : (
                                            ''
                                        )}
                                    </span>
                                </td>
                                <td className={classnames(formStyles.positionenCell, formStyles.positionenTextCell)}>
                                    {lieferungPosition.posText}
                                    {lieferungPosition.posBem ? (
                                        <i>
                                            <br />
                                            {lieferungPosition.posBem}
                                        </i>
                                    ) : (
                                        ''
                                    )}
                                </td>
                                <td className={classnames(formStyles.positionenCell, formStyles.positionenEinhCell)}>
                                    {lieferungPosition.posEinh
                                        ? einheitenByCode[lieferungPosition.posEinh].text || lieferungPosition.posEinh
                                        : '?'}
                                </td>
                                <td className={classnames(formStyles.positionenCell, formStyles.positionenMengeCell)}>
                                    {formatDecimal(lieferungPosition.posMenge, 3)}
                                </td>
                                <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                    {formatDecimal(lieferungPosition.posAMenge, 3)}
                                </td>
                                <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                    {formatMoney(lieferungPosition.posEkPreis, 3)}
                                </td>
                                <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                    {formatMoney(lieferungPosition.posEPreis, 3)}
                                </td>
                                <td
                                    className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}
                                    title={'VK-Brutto: ' + formatMoney(lieferungPosition.posBrutto)}
                                >
                                    {formatMoney(lieferungPosition.posNetto)}
                                </td>
                                {/*
                                <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                    {formatMoney(lieferungPosition.posBrutto)}
                                </td>
                                */}
                                <td
                                    className={classnames(formStyles.positionenCell, formStyles.positionenMwstCell)}
                                    title={'VK-Brutto: ' + formatMoney(lieferungPosition.posBrutto)}
                                >
                                    {formatPercent(lieferungPosition.posMwst)}
                                </td>
                                <td className={classnames(formStyles.positionenCell, formStyles.positionenLArtCell)}>
                                    {lieferungPosition.lArt}
                                </td>
                                {allowEdit && (
                                    <>
                                        <td
                                            className={classnames(
                                                formStyles.positionenCell,
                                                formStyles.positionenActionCell,
                                            )}
                                        >
                                            {lieferungPosition.pos > 1 && (
                                                <UpIcon
                                                    className={formStyles.positionenAction}
                                                    onClick={async () => {
                                                        await moveUpLieferungPosition(
                                                            lieferungPosition.lNr,
                                                            lieferungPosition.pos,
                                                        );
                                                        if (refetch) {
                                                            refetch();
                                                        }
                                                    }}
                                                />
                                            )}
                                        </td>
                                        <td
                                            className={classnames(
                                                formStyles.positionenCell,
                                                formStyles.positionenActionCell,
                                            )}
                                        >
                                            <EditIcon
                                                className={formStyles.positionenAction}
                                                onClick={() => setLieferungPositionEditData(lieferungPosition)}
                                            />
                                        </td>
                                        <td
                                            className={classnames(
                                                formStyles.positionenCell,
                                                formStyles.positionenActionCell,
                                            )}
                                        >
                                            <DeleteIcon
                                                className={formStyles.positionenAction}
                                                onClick={() => setLieferungPositionDeleteData(lieferungPosition)}
                                            />
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                </tbody>
            </table>
            {lieferungPositionEditData !== null && (
                <LieferungPositionEdit
                    kunde={kunde}
                    lieferung={lieferung}
                    lNr={lieferungPositionEditData.lNr}
                    pos={lieferungPositionEditData.pos}
                    onClose={(_refetch) => {
                        setLieferungPositionEditData(null);
                        if (_refetch && refetch) {
                            refetch();
                        }
                    }}
                />
            )}
            {lieferungPositionDeleteData !== null && (
                <LieferungPositionDelete
                    lieferungPosition={lieferungPositionDeleteData}
                    onClose={(_refetch) => {
                        setLieferungPositionDeleteData(null);
                        if (_refetch && refetch) {
                            refetch();
                        }
                    }}
                />
            )}
        </>
    );
};
